/**
 * App chrome and the onboarding surfaces, English.
 *
 * Scope note: this covers what a saver sees on *every* screen — the tab bar,
 * the header, the account menu, the home quick actions and the page titles —
 * plus the two screens the production-readiness review singled out (F-35): the
 * self-registration wizard and the identity-capture form it leads into, which
 * were hardcoded English on a product that offers a language switch in its own
 * account drawer. Other screen bodies are still hardcoded and get pulled in
 * here screen by screen. Keys are grouped by surface so a translator can work
 * through one surface at a time rather than a flat wall of strings.
 *
 * `localeParity.test.ts` holds every language to this file's key set, because
 * i18next falls back to English silently and a drifting bundle would otherwise
 * ship a half-translated screen with nothing failing.
 */
export default {
  // Bottom tab bar
  'nav.home': 'Home',
  'nav.schemes': 'Schemes',
  'nav.pay': 'Pay',
  'nav.ledger': 'Ledger',
  'nav.account': 'Account',

  // Top app bar
  'header.notifications': 'Notifications',
  'header.notificationsUnread': 'Notifications, {{count}} unread',
  'header.back': 'Go back',
  'header.theme.toLight': 'Switch to light theme',
  'header.theme.toDark': 'Switch to dark theme',
  'header.language': 'Language',
  'header.language.en': 'English',
  'header.language.ml': 'Malayalam',

  // Home quick actions
  'quickActions.goals': 'Goals',
  'quickActions.redeem': 'Redeem',
  'quickActions.loans': 'Loans',
  'quickActions.complaints': 'Complaints',

  // Account menu + the page titles those rows lead to
  'account.profile': 'Profile & KYC',
  'account.ledger': 'Account Ledger',
  'account.goals': 'Goals & Milestones',
  'account.payments': 'Payment History',
  'account.redemptions': 'Redemptions',
  'account.loans': 'Jewellery Loans',
  'account.complaints': 'Complaints & Support',
  'account.notifications': 'Notifications',
  'account.settings': 'Settings',
  'account.help': 'Help & Support',

  // Profile & KYC screen — the editable D10 demographics (F-24)
  'profile.mobile': 'Mobile Number',
  'profile.mobileLocked': 'Your mobile number is your login identity and cannot be changed here.',
  'profile.dateOfBirth': 'Date of Birth',
  'profile.address': 'Address',
  'profile.addressLine1': 'House / flat no., street',
  'profile.addressLine2': 'Area, locality (optional)',
  'profile.addressCity': 'City',
  'profile.addressState': 'State',
  'profile.addressPincode': 'Pincode',
  'profile.addressIncomplete': 'Enter line 1, city, state and a 6-digit pincode — or clear them all.',

  // Profile & KYC screen — chrome (F-35)
  'profile.edit': 'Edit Profile',
  'profile.personalInfo': 'Personal Info',
  'profile.fullName': 'Full Name',
  'profile.email': 'Email Address',
  'profile.cancel': 'Cancel',
  'profile.save': 'Save Changes',
  'profile.saving': 'Saving…',
  'profile.submitKyc': 'Submit KYC',
  'profile.submitKycSubtitle': 'Add your Aadhaar and PAN with a photo of each card',
  'profile.kycStanding': 'KYC Standing',
  'profile.kycStatus': 'Status',
  'profile.kycTier': 'Tier',
  'profile.kycTransactions': 'Transactions',
  'profile.kycEnabled': 'Enabled',
  'profile.kycNotEnabled': 'Not enabled',
  'profile.kycLastApproved': 'Last Approved',
  'profile.kycNextReview': 'Next Review Due',
  'profile.kycUnavailable': 'KYC details are unavailable right now.',

  // KYC standing, as the customer is told it (F-35)
  'kyc.status.notStarted': 'Not Started',
  'kyc.status.submitted': 'Submitted',
  'kyc.status.underReview': 'Under Review',
  'kyc.status.approved': 'Verified',
  'kyc.status.rejected': 'Rejected',
  'kyc.status.reKycDue': 'Re-verification Due',
  'kyc.status.restricted': 'Restricted',
  'kyc.message.notStarted': 'Complete your KYC to start saving gold.',
  'kyc.message.submitted': 'Your documents were received and are queued for review.',
  'kyc.message.underReview': 'Our team is reviewing your documents. This usually takes up to 24 hours.',
  'kyc.message.approved': 'Your identity is verified. You are all set.',
  'kyc.message.amendmentPending': 'Your identity is verified. A recent update to your documents is being reviewed.',
  'kyc.message.rejected': 'Your submission was rejected. Please submit your documents again.',
  'kyc.message.reKycDue': 'A periodic re-verification is due. Please submit your documents again.',
  'kyc.message.restricted': 'Your account is restricted. Please contact support.',
  'kyc.tier.0': 'Tier 0',
  'kyc.tier.1': 'Tier 1',
  'kyc.tier.2': 'Tier 2',
  'kyc.tier.3': 'Tier 3',

  // The identity-capture form (F-35). "Aadhaar" and "PAN" stay as they are — that is what the
  // cards say, in every language this app ships.
  'kyc.aadhaarNumber': 'Aadhaar Number',
  'kyc.aadhaarInvalid': 'Aadhaar is 12 digits',
  'kyc.aadhaarPhoto': 'Aadhaar Card Photo',
  'kyc.tapToPhotograph': 'Tap to photograph',
  'kyc.photoPreview': '{{label}} preview',
  'kyc.removePhoto': 'Remove photo',
  'kyc.privacyNote': 'Your document numbers and card photos are sent securely for verification. Numbers are never stored in plain form, and photos are deleted once your KYC is approved.',
  'kyc.fileWrongType': 'That file is a {{type}}. Photograph the card again, or choose a JPEG or PNG.',
  'kyc.fileTooLarge': 'That photo is {{size}}, over the {{limit}} limit. Try photographing the card on its own, closer in.',

  // The second officially-valid document, now a choice (F-28). Document names are as the cards
  // print them — a passport says "Passport" in English on the document itself.
  'kyc.secondDocument': 'Second document',
  'kyc.secondDocumentHint': 'Any one of these, alongside your Aadhaar.',
  'kyc.document.PAN': 'PAN card',
  'kyc.document.PASSPORT': 'Passport',
  'kyc.document.DRIVING_LICENCE': 'Driving licence',
  'kyc.document.VOTER_ID': 'Voter ID',
  'kyc.documentNumber': '{{document}} number',
  'kyc.documentPhoto': '{{document}} photo',
  'kyc.documentInvalid': 'A {{document}} number looks like {{example}}',

  // Self-registration wizard (F-35)
  'signup.createAccount': 'Create Account',
  'signup.createAccountSubtitle': 'Start your gold savings journey',
  'signup.optional': '(optional)',
  'signup.fullName': 'Full Name',
  'signup.fullNamePlaceholder': 'Ravi Kumar',
  'signup.email': 'Email Address',
  'signup.emailPlaceholder': 'ravi.kumar@example.com',
  'signup.emailInvalid': 'Please enter a valid email address',
  'signup.mobile': 'Mobile Number',
  'signup.mobilePlaceholder': '98765 43210',
  'signup.mobileHint': 'We will send a verification OTP to this number',
  'signup.agentCode': 'Agent code',
  'signup.agentCodeChecking': 'Checking code…',
  'signup.agentCodeResolved': 'Referred by {{name}}',
  'signup.agentCodeResolvedWithPartner': 'Referred by {{name}} — {{partner}}',
  'signup.agentCodeUnknown': 'We couldn’t confirm this code — you can still continue.',
  'signup.back': 'Back',
  'signup.reviewTitle': 'Review & Verify',
  'signup.reviewSubtitle': 'Confirm your details before proceeding',
  'signup.personalInfo': 'Personal Info',
  'signup.name': 'Name',
  'signup.mobileShort': 'Mobile',
  'signup.emailShort': 'Email',
  'signup.termsBefore': 'I have read and accept the',
  'signup.termsLink': 'terms and conditions',
  'signup.termsAfter': ', including the processing of the identity documents I provide for KYC.',
  'signup.marketing': 'Send me offers and updates by WhatsApp and email. Optional.',
  'signup.marketingFromTenant': 'Send me offers and updates from {{appName}} by WhatsApp and email. Optional.',
  'signup.otpNoticeTo': 'We will send an OTP to',
  'signup.kycTitle': 'Verify your identity',
  'signup.kycSubtitle': 'Add your Aadhaar and PAN with a photo of each card. You can also finish this later from your Profile.',
  'signup.kycSubmit': 'Submit for verification',
  'signup.kycUploading': 'Uploading…',
  'signup.kycSkip': 'Skip for now — finish later from Profile',
  'signup.welcome': 'Welcome!',
  'signup.welcomeTenant': 'Welcome to {{appName}}!',
  'signup.welcomeKyc': 'Your account is ready and your KYC documents are under review. You can start once they are approved.',
  'signup.welcomeNoKyc': 'Your account has been created successfully. Start your gold savings journey today.',
  'signup.goToDashboard': 'Go to Dashboard',
  'signup.pleaseWait': 'Please wait…',
  'signup.sendOtp': 'Send OTP',
  'signup.continue': 'Continue',
  'signup.haveAccount': 'Already have an account? Sign in',

  // Page titles that are not account-menu rows
  'page.pay': 'Pay',
  'page.spin': 'Spin & Win',
  'page.spinGold': 'Gold Saver Spin'
};
